import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from './ui/card'
import { Button } from './ui/button'
import { Badge } from './ui/badge'
import { quotesApi, customersApi } from '../services/api'
import { FileText, Users, TrendingUp, PlusCircle, ArrowRight, Clock, CheckCircle2, Send, XCircle, Building2 } from 'lucide-react'

const statusConfig = {
  'Entwurf': { color: 'bg-slate-100 text-slate-600', icon: Clock },
  'Versendet': { color: 'bg-slate-100 text-slate-600', icon: Send },
  'Angenommen': { color: 'bg-emerald-50 text-emerald-700', icon: CheckCircle2 },
  'Abgelehnt': { color: 'bg-slate-100 text-slate-500', icon: XCircle },
}

const Dashboard = ({ user, activeCompany, onNavigate, onNewQuote }) => {
  const [stats, setStats] = useState({ quotes: 0, accepted: 0, sent: 0, customers: 0, revenue: 0 })
  const [recentQuotes, setRecentQuotes] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!activeCompany?.id) {
      setLoading(false)
      return
    }
    loadDashboardData()
  }, [activeCompany?.id])

  const loadDashboardData = async () => {
    setLoading(true)
    try {
      const [quotesRes, customersRes] = await Promise.all([
        quotesApi.getAll(activeCompany.id).catch(() => ({ data: [] })),
        customersApi.getAll(activeCompany.id).catch(() => ({ data: [] })),
      ])

      const quotes = quotesRes.data || []
      const customers = customersRes.data || []
      const accepted = quotes.filter(q => q.status === 'Angenommen')
      const sent = quotes.filter(q => q.status === 'Versendet')
      const totalRevenue = accepted.reduce((sum, q) => sum + (q.total || 0), 0)

      setStats({
        quotes: quotes.length,
        accepted: accepted.length,
        sent: sent.length,
        customers: customers.length,
        revenue: totalRevenue,
      })

      const sorted = [...quotes].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      setRecentQuotes(sorted.slice(0, 6))
    } catch (err) {
      console.error('Dashboard load error:', err)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (val) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(val || 0)
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-'

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (!activeCompany) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-16 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-slate-100 mb-6">
          <Building2 className="h-8 w-8 text-slate-400" />
        </div>
        <h2 className="text-2xl font-bold text-slate-800 mb-2">Willkommen bei offfer</h2>
        <p className="text-slate-500 mb-8 max-w-md mx-auto">
          Legen Sie zunächst Ihre erste Firma an, um mit der Angebotserstellung zu starten.
        </p>
        <Button onClick={() => onNavigate('companies')} className="bg-slate-900 hover:bg-slate-800 text-white gap-2 text-base px-8 py-3 h-auto">
          <PlusCircle className="h-5 w-5" />
          Erste Firma anlegen
        </Button>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            Willkommen, {user?.full_name?.split(' ')[0] || 'zurück'}
          </h1>
          <p className="text-slate-500 mt-1">
            {activeCompany?.name || 'Keine Firma ausgewählt'}
          </p>
        </div>
        <Button onClick={onNewQuote} className="bg-amber-500 hover:bg-amber-400 text-white gap-2 shadow-sm shadow-amber-200">
          <PlusCircle className="h-4 w-4" />
          Neues Angebot
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <KPICard title="Angebote gesamt" value={stats.quotes} icon={FileText} color="blue" onClick={() => onNavigate('quotes')} />
        <KPICard title="Versendet" value={stats.sent} icon={Send} color="amber" subtitle="Warten auf Antwort" />
        <KPICard title="Angenommen" value={stats.accepted} icon={CheckCircle2} color="green"
          subtitle={stats.quotes > 0 ? `${Math.round(stats.accepted / stats.quotes * 100)}% Quote` : ''} />
        <KPICard title="Kunden" value={stats.customers} icon={Users} color="purple" onClick={() => onNavigate('customers')} />
      </div>

      {/* Revenue Card */}
      <Card className="mb-8 border border-slate-200 bg-white">
        <CardContent className="py-6 flex items-center justify-between">
          <div>
            <p className="text-slate-500 text-sm font-medium">Umsatz aus Angeboten</p>
            <p className="text-3xl font-bold mt-1 text-slate-900">{formatCurrency(stats.revenue)}</p>
            <p className="text-slate-400 text-sm mt-1">Aus {stats.accepted} angenommenen Angeboten</p>
          </div>
          <TrendingUp className="h-10 w-10 text-slate-200" />
        </CardContent>
      </Card>

      {/* Recent Quotes + Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-4">
              <CardTitle className="text-lg">Letzte Angebote</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => onNavigate('quotes')} className="text-slate-500 hover:text-slate-700 gap-1">
                Alle anzeigen <ArrowRight className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent>
              {recentQuotes.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <FileText className="h-10 w-10 mx-auto mb-3 opacity-50" />
                  <p>Noch keine Angebote vorhanden</p>
                  <Button variant="outline" size="sm" onClick={onNewQuote} className="mt-3 gap-1">
                    <PlusCircle className="h-4 w-4" /> Erstes Angebot erstellen
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {recentQuotes.map((quote) => {
                    const cfg = statusConfig[quote.status] || statusConfig['Entwurf']
                    const StatusIcon = cfg.icon
                    return (
                      <div key={quote.id || quote._id}
                        className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 transition-colors cursor-pointer border border-slate-100 group"
                        onClick={() => onNavigate('quotes')}>
                        <div className="flex items-center gap-3 min-w-0">
                          <div className={`p-2 rounded-lg ${cfg.color}`}>
                            <StatusIcon className="h-4 w-4" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-slate-900 truncate">
                              {quote.customerCompany || quote.customerName || 'Unbenannt'}
                            </p>
                            <p className="text-sm text-slate-500">
                              {quote.quoteNumber} · {formatDate(quote.createdAt)}
                            </p>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0 ml-4">
                          <p className="font-semibold text-slate-900">{formatCurrency(quote.total)}</p>
                          <Badge variant="secondary" className={`text-xs ${cfg.color}`}>{quote.status}</Badge>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div>
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-lg">Schnellzugriff</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <QuickAction icon={PlusCircle} label="Neues Angebot" onClick={onNewQuote} color="blue" />
              <QuickAction icon={Users} label="Kunden verwalten" onClick={() => onNavigate('customers')} color="purple" />
              <QuickAction icon={Building2} label="Firmen verwalten" onClick={() => onNavigate('companies')} color="indigo" />
              <QuickAction icon={FileText} label="Alle Angebote" onClick={() => onNavigate('quotes')} color="slate" />
              <QuickAction icon={TrendingUp} label="Einnahmen" onClick={() => onNavigate('revenue')} color="green" />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}

const KPICard = ({ title, value, icon: Icon, color, subtitle, onClick }) => {
  return (
    <Card className={`border-slate-200 ${onClick ? 'cursor-pointer hover:border-slate-300 transition-colors' : ''}`} onClick={onClick}>
      <CardContent className="pt-5 pb-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-medium text-slate-400 uppercase tracking-wide">{title}</p>
            <p className="text-2xl font-mono font-semibold text-slate-900 mt-1.5 tracking-tight">{value}</p>
            {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
          </div>
          <Icon className="h-5 w-5 text-slate-300 mt-0.5" />
        </div>
      </CardContent>
    </Card>
  )
}

const QuickAction = ({ icon: Icon, label, onClick, color }) => {
  return (
    <button onClick={onClick}
      className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors text-left">
      <div className="p-1.5 rounded-md bg-slate-100">
        <Icon className="h-4 w-4 text-slate-500" />
      </div>
      <span className="text-sm font-medium text-slate-600">{label}</span>
    </button>
  )
}

export default Dashboard
